import React, { useState } from 'react'
import {
    CloseButton,
    Button,
} from 'components'
import { BookingTile } from 'views/HomePage/BookingTile'
import { ContentForm } from 'views/AppPage/ContentForm'
import { AnalyticsEvent, AppData, ErrorMessage, PromptOptions } from 'app/types'
import { analyticsEvent, showPrompt, submitBooking } from 'actions/appActions'
import { useAppDispatch } from 'app/hooks'
import { packageFormData } from 'helpers/requestHandler'
import { logger } from 'helpers/logger'
import { marketingFormOptions, reportMarketingEvent, resolveMarketingEventLink } from 'helpers/marketingTracking'
import { ReactMarkdown } from 'react-markdown/lib/react-markdown'

interface BookingDialogProps {
    app: AppData,
    organization: OrganizationData,
    title: string,
    subtitle: string,
    error: string,
    message: string,
    onSubmit: (formData: Dict) => void,
    onClose?: () => void,
    animation?: string,
    analyticsEventTypeId: AnalyticsEvent,
    submitMessage: string,
    fields: Dict,
    formOptions: Dict,
    promptOptions: Dict,
    tags: string[],
}

export function FormDialog(props: BookingDialogProps) {
    const {
        app,
        organization,
        title,
        subtitle,
        fields,
        formOptions,
        promptOptions,
        message,
        onSubmit,
        onClose,
        animation,
        analyticsEventTypeId,
        submitMessage,
        tags,
    } = { submitMessage: 'Submit', ...props }

    const dispatch = useAppDispatch()
    const [submitRef, setSubmitRef] = useState(null)
    const [formFilled, setFormFilled] = useState(0)
    const [error, setError] = useState(null)
    const [loaded, setLoaded] = useState(false)


    function handleClose() {
        onClose()
    }

    function handleSubmit(formData) {
        if(onSubmit) {
            return onSubmit(formData)
        } else {
            return submitFormDefault(formData)
        }
    }

    function submitFormDefault(formData) {
        const { finalFormData, promptOptions, finalFormOptions } = packageFormData({ ...formData, tags: tags ? (Array.isArray(tags) ? [...tags] : tags) : [] }, { ...formOptions, fields }, analyticsEventTypeId)
        // A native Request Info dialog can be opened from several reusable
        // components. Preserve an explicitly selected CTA label, or fall back
        // to the configured Request Info link for callers that do not supply
        // tracking options themselves.
        const trackingOptions = finalFormOptions.marketingTrackingEnabled || finalFormOptions.marketing_tracking_enabled
            ? finalFormOptions
            : { ...finalFormOptions, ...marketingFormOptions(resolveMarketingEventLink(app, organization, 'request_info')) }
        /*const finalFormData = { ...formData }
        if (finalFormData.address) {
            finalFormData.address = `${finalFormData.address}, ${finalFormData.city} ${finalFormData.province}, ${finalFormData.postalcode}`
        } else if (finalFormData.city) {
            finalFormData.address = finalFormData.city
        } else {
            finalFormData.address = ''
        }

        if (formData.firstname && formData.lastname) {
            finalFormData.name = `${formData.firstname} ${formData.lastname}`
        }
        finalFormData.optIn = finalFormData.consent == true

        finalFormData.message = []

        const commentKey = Object.keys(finalFormData).find((key) => key.includes('comment'))
        finalFormData.message.push(finalFormData[commentKey])

        const consent = fields.find((x) => x.id == 'consent')
        if (consent && !consent.required) {
            finalFormData.message.push(`Opt In To Communication: ${finalFormData.consent ? 'Yes' : 'No'}`)
        }
        finalFormData.message.push(`Request Type: ${AnalyticsEvent[analyticsEventTypeId].toReadable()}`)*/

        // Keep the submission acknowledgement visible, but do not make
        // tracking wait for the visitor to dismiss it. A successful server
        // response is the precise point at which a native lead is reportable.
        const prompt = dispatch(showPrompt(promptOptions))
        return dispatch(submitBooking({ app, data: finalFormData, options: finalFormOptions }))
            .then(async (b) => {
                try {
                    if (!b.payload.success) {
                        throw b.payload.message
                    } else {
                        if (trackingOptions.marketingTrackingEnabled || trackingOptions.marketing_tracking_enabled) {
                            await reportMarketingEvent({
                                app,
                                organization,
                                eventName: trackingOptions.marketingEventName || trackingOptions.marketing_event_name || 'form_submit',
                                label: trackingOptions.marketingLabel || trackingOptions.marketing_label,
                                formId: trackingOptions.formId || trackingOptions.form_id,
                                conversionActionId: trackingOptions.marketingConversionActionId || trackingOptions.marketing_conversion_action_id,
                                emailField: trackingOptions.marketingEmailField || trackingOptions.marketing_email_field || 'email',
                                phoneField: trackingOptions.marketingPhoneField || trackingOptions.marketing_phone_field || 'phone',
                                consentField: trackingOptions.marketingConsentField || trackingOptions.marketing_consent_field || 'consent',
                                source: 'native_form',
                                contact: finalFormData,
                                consent: finalFormData.consent === true || finalFormData.consent === 'true' || finalFormData.consent === '1',
                                mappingEnabled: true,
                            })
                        }
                        if (finalFormData.analyticsEventTypeId > 0) {
                            // The event type is attached to the packaged form data.
                            // The dialog prop is not populated for the reusable
                            // Request Info prompt, so using it can throw before
                            // the marketing event is emitted.
                            dispatch(analyticsEvent(finalFormData.analyticsEventTypeId))
                        }
                        await prompt
                        return true
                    }
                } catch (e) {
                    throw e
                }
                return false
            }).catch((e) => {
                logger.error("Booking error", e)
                throw ErrorMessage.BookingError
            })
    }
    const errors = [error, props.error].filter((x) => x)

    return <div className={`dialog ${animation}`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="title">
            <h3>{title}</h3>
            {subtitle && <h4>{subtitle}</h4>}
            {errors.length > 0 && <h5 className="error">{errors.join(', ')}</h5>}
        </div>
        <div className="dialog-wrapper">
            {message && <span className="message"><ReactMarkdown>{message}</ReactMarkdown></span>}
            <div className="row">
                <ContentForm onSubmit={handleSubmit} onLoad={setLoaded} onError={setError} submitRef={submitRef} {...formOptions} fields={fields} onFormFilled={setFormFilled} preventNav={false} />
            </div>
            <Button tertiary icon="far fa-paper-plane" className={!loaded ? 'disabled' : ''} forwardRef={setSubmitRef}>{submitMessage}</Button>
            <CloseButton large onClick={handleClose} />
        </div>
    </div>
}
